// TablePagination.tsx
import React from 'react';

interface TablePaginationProps {
  table: any;
}

const TablePagination: React.FC<TablePaginationProps> = ({ table }) => {
  const { state, pageCount, gotoPage, memoizedData } = table;

  const currentPage = state.pageIndex;
  const totalPages = pageCount;

  if (totalPages <= 1) return null;

  const visiblePages = 5;

  const getPageNumbers = () => {
    let start = Math.max(1, currentPage + 1 - Math.floor(visiblePages / 2));
    let end = Math.min(totalPages, start + visiblePages - 1);

    if (end === totalPages) {
      start = Math.max(1, end - visiblePages + 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className="flex flex-col border-t border-stroke p-2 px-4 py-4 dark:border-strokedark md:flex-row md:justify-between">
      <p className="text-gray-700 text-center text-sm font-medium dark:text-white md:text-left">
        Mostrando registros del {currentPage * state.pageSize + 1} al{' '}
        {Math.min((currentPage + 1) * state.pageSize, memoizedData.length)} de
        un total de {memoizedData.length} registros
      </p>

      <nav className="mt-2 flex justify-center md:mt-0">
        <ul className="flex items-center">
          {/* Botón Anterior */}
          <button
            onClick={() => gotoPage(Math.max(currentPage - 1, 0))}
            disabled={currentPage === 0}
            aria-label="Página anterior"
            className="flex h-9 w-9 items-center justify-center rounded-l-md border border-stroke hover:border-primary hover:bg-gray hover:text-primary disabled:opacity-50 dark:border-strokedark dark:hover:border-primary dark:hover:bg-graydark"
          >
            <svg
              className="fill-current"
              width="8"
              height="16"
              viewBox="0 0 8 16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M7.17578 15.1156C7.00703 15.1156 6.83828 15.0593 6.72578 14.9187L0.369531 8.44995C0.116406 8.19683 0.116406 7.80308 0.369531 7.54995L6.72578 1.0812C6.97891 0.828076 7.37266 0.828076 7.62578 1.0812C7.87891 1.33433 7.87891 1.72808 7.62578 1.9812L1.71953 7.99995L7.65391 14.0187C7.90703 14.2718 7.90703 14.6656 7.65391 14.9187C7.48516 15.0312 7.34453 15.1156 7.17578 15.1156Z" />
            </svg>
          </button>

          {/* Primera página */}
          {pages[0] > 1 && (
            <>
              <button
                onClick={() => gotoPage(0)}
                className={`flex h-9 w-9 items-center justify-center border border-stroke px-4 py-[5px] font-medium hover:border-primary hover:bg-gray hover:text-primary dark:border-strokedark dark:hover:border-primary dark:hover:bg-graydark ${
                  currentPage === 0 ? 'bg-primary text-white' : ''
                }`}
              >
                1
              </button>
              {pages[0] > 2 && <span className="px-2">...</span>}
            </>
          )}

          {/* Páginas dinámicas */}
          {pages.map((num) => (
            <button
              key={num}
              onClick={() => gotoPage(num - 1)}
              className={`flex h-9 w-9 items-center justify-center border border-stroke px-4 py-[5px] font-medium hover:border-primary hover:bg-gray hover:text-primary dark:border-strokedark dark:hover:border-primary dark:hover:bg-graydark ${
                currentPage === num - 1 ? 'bg-primary text-white' : ''
              }`}
            >
              {num}
            </button>
          ))}

          {/* Última página */}
          {pages[pages.length - 1] < totalPages && (
            <>
              {pages[pages.length - 1] < totalPages - 1 && (
                <span className="px-2">...</span>
              )}
              <button
                onClick={() => gotoPage(totalPages - 1)}
                className={`flex h-9 w-9 items-center justify-center border border-stroke px-4 py-[5px] font-medium hover:border-primary hover:bg-gray hover:text-primary dark:border-strokedark dark:hover:border-primary dark:hover:bg-graydark ${
                  currentPage === totalPages - 1 ? 'bg-primary text-white' : ''
                }`}
              >
                {totalPages}
              </button>
            </>
          )}

          {/* Botón Siguiente */}
          <button
            onClick={() => gotoPage(Math.min(currentPage + 1, totalPages - 1))}
            disabled={currentPage >= totalPages - 1}
            aria-label="Página siguiente"
            className="flex h-9 w-9 items-center justify-center rounded-r-md border border-stroke hover:border-primary hover:bg-gray hover:text-primary disabled:opacity-50 dark:border-strokedark dark:hover:border-primary dark:hover:bg-graydark"
          >
            <svg
              className="fill-current"
              width="8"
              height="16"
              viewBox="0 0 8 16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0.819531 15.1156C0.650781 15.1156 0.510156 15.0593 0.369531 14.9468C0.116406 14.6937 0.116406 14.3 0.369531 14.0468L6.27578 7.99995L0.369531 1.9812C0.116406 1.72808 0.116406 1.33433 0.369531 1.0812C0.622656 0.828076 1.01641 0.828076 1.26953 1.0812L7.62578 7.54995C7.87891 7.80308 7.87891 8.19683 7.62578 8.44995L1.26953 14.9187C1.15703 15.0312 0.988281 15.1156 0.819531 15.1156Z" />
            </svg>
          </button>
        </ul>
      </nav>
    </div>
  );
};

export default TablePagination;
